import { Injectable } from '@angular/core';
import { pcsRxStompConfig } from '@app/core/config/web-socket.config';
import { AUTHORIZATION_HEADER_NAME, CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from '@app/core/services/auth.constants';
import { AuthService } from '@app/core/services/auth.service';
import { environment } from '@env/environment';
import { RxStompService } from '@stomp/ng2-stompjs';
import { RxStompState } from '@stomp/rx-stomp';
import { IMessage } from '@stomp/stompjs';
import { CookieService } from 'ngx-cookie-service';
import * as R from 'ramda';
import { merge, Observable, Subject } from 'rxjs';
import { filter, mapTo, scan } from 'rxjs/operators';

const incrementRetries = mapTo(R.add(1));
const maxRetries = mapTo(R.always(environment.webSocketMaxConnectRetries));
const resetRetries = mapTo(R.always(0));

@Injectable()
export class WebSocketService {

  connect() {
    if (!this.rxStompService.connected()) {
      this.rxStompService.configure({...pcsRxStompConfig, connectHeaders: this.headers()});
      this.rxStompService.activate();
    }
  }

  disconnect() {
    this.rxStompService.deactivate();
  }

  publish(destination, body) {
    this.rxStompService.publish({destination, body, headers: this.headers()});
  }

  watch(destination): Observable<IMessage> {
    return this.rxStompService.watch(destination, this.headers());
  }

  private headers() {
    return {
      [AUTHORIZATION_HEADER_NAME]: this.authService.getToken(),
      [CSRF_HEADER_NAME]: this.cookieService.get(CSRF_COOKIE_NAME)
    };
  }

  constructor(private authService: AuthService,
    private cookieService: CookieService,
    private rxStompService: RxStompService) {
    const disconnectedSubject = new Subject();
    const connected$ = this.rxStompService.connected$.pipe(maxRetries);
    const disconnected$ = disconnectedSubject.asObservable().pipe(resetRetries);
    const retry$ = this.rxStompService.connectionState$.pipe(
      filter((state: number) => R.equals(state, RxStompState.CLOSED)),
      incrementRetries
    );

    merge(retry$, connected$, disconnected$)
      .pipe(
        scan((currentValue, applyFunction) => applyFunction(currentValue), 0),
        filter(numOfRetries => R.gt(numOfRetries, environment.webSocketMaxConnectRetries))
      ).subscribe(() => {
        this.rxStompService.deactivate();
        disconnectedSubject.next(null);
      });
  }
}
